<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import SettingsPageLayout from 'dashboard/components-next/ailab/SettingsPageLayout.vue';
import SettingsHeader from 'dashboard/components-next/ailab/pageComponents/settings/SettingsHeader.vue';
import AssistantBasicSettingsForm from 'dashboard/components-next/ailab/pageComponents/assistant/settings/AssistantBasicSettingsForm.vue';
import AssistantSystemSettingsForm from 'dashboard/components-next/ailab/pageComponents/assistant/settings/AssistantSystemSettingsForm.vue';
import AssistantControlItems from 'dashboard/components-next/ailab/pageComponents/assistant/settings/AssistantControlItems.vue';
import EditAssistantDialog from 'dashboard/components-next/ailab/svo/EditAssistantDialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const assistantId = route.params.assistantId;
const uiFlags = useMapGetter('ailabAssistants/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingItem);
const assistant = computed(() =>
  store.getters['ailabAssistants/getRecord'](Number(assistantId))
);

const isAssistantAvailable = computed(() => !!assistant.value?.id);

// SVO Edit Dialog
const editAssistantDialog = ref(null);

const openSvoEditDialog = () => {
  nextTick(() => {
    editAssistantDialog.value?.open();
  });
};

const controlItems = computed(() => {
  return [
    {
      name: 'Product Info',
      description:
        'Manage product information, media, testimonials, and answer templates',
      routeName: 'ailab_product_info',
      icon: 'i-lucide-package',
    },
    {
      name: t(
        'AILAB.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.GUARDRAILS.TITLE'
      ),
      description: t(
        'AILAB.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.GUARDRAILS.DESCRIPTION'
      ),
      routeName: 'ailab_assistants_guardrails_index',
    },
    {
      name: t(
        'AILAB.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.SCENARIOS.TITLE'
      ),
      description: t(
        'AILAB.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.SCENARIOS.DESCRIPTION'
      ),
      routeName: 'ailab_assistants_scenarios_index',
    },
    {
      name: t(
        'AILAB.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.RESPONSE_GUIDELINES.TITLE'
      ),
      description: t(
        'AILAB.ASSISTANTS.SETTINGS.CONTROL_ITEMS.OPTIONS.RESPONSE_GUIDELINES.DESCRIPTION'
      ),
      routeName: 'ailab_assistants_guidelines_index',
    },
  ];
});

const breadcrumbItems = computed(() => {
  const activeControlItem = controlItems.value?.find(
    item => item.routeName === route.name
  );

  return [
    {
      label: t('AILAB.ASSISTANTS.SETTINGS.BREADCRUMB.ASSISTANT'),
      routeName: 'ailab_assistants_index',
    },
    { label: assistant.value?.name, routeName: 'ailab_assistants_edit' },
    ...(activeControlItem
      ? [
          {
            label: activeControlItem.name,
            routeName: activeControlItem.routeName,
          },
        ]
      : []),
  ];
});

const handleSubmit = async updatedAssistant => {
  try {
    await store.dispatch('ailabAssistants/update', {
      id: assistantId,
      ...updatedAssistant,
    });
    useAlert(t('AILAB.ASSISTANTS.EDIT.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('AILAB.ASSISTANTS.EDIT.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

onMounted(() => {
  if (!isAssistantAvailable.value) {
    store.dispatch('ailabAssistants/show', assistantId);
  }
});
</script>

<template>
  <SettingsPageLayout
    :breadcrumb-items="breadcrumbItems"
    :is-fetching="isFetching"
    class="[&>div]:max-w-[80rem]"
  >
    <template #body>
      <div class="flex flex-col gap-6">
        <!-- SVO Style Quick Edit Button -->
        <div
          class="relative flex items-center justify-between gap-4 p-5 rounded-2xl border border-slate-200 bg-white overflow-hidden"
        >
          <div class="absolute inset-0 pointer-events-none">
            <div
              class="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-70 bg-indigo-100"
            />
          </div>
          <div class="flex flex-col gap-1">
            <h3 class="relative text-sm font-semibold text-slate-900">
              SVO.ai configuration
            </h3>
            <p class="relative text-xs text-slate-600">
              Configure personality, prompt sections, and advanced behavior.
            </p>
          </div>
          <Button
            icon="i-lucide-settings-2"
            label="Configure"
            size="sm"
            variant="outline"
            color="slate"
            @click="openSvoEditDialog"
          />
        </div>

        <div class="flex flex-col gap-6">
          <SettingsHeader
            :heading="t('AILAB.ASSISTANTS.SETTINGS.BASIC_SETTINGS.TITLE')"
            :description="
              t('AILAB.ASSISTANTS.SETTINGS.BASIC_SETTINGS.DESCRIPTION')
            "
          />
          <AssistantBasicSettingsForm
            :assistant="assistant"
            @submit="handleSubmit"
          />
        </div>
        <span class="h-px w-full bg-slate-200 mt-2" />
        <div class="flex flex-col gap-6">
          <SettingsHeader
            :heading="t('AILAB.ASSISTANTS.SETTINGS.SYSTEM_SETTINGS.TITLE')"
            :description="
              t('AILAB.ASSISTANTS.SETTINGS.SYSTEM_SETTINGS.DESCRIPTION')
            "
          />
          <AssistantSystemSettingsForm
            :assistant="assistant"
            @submit="handleSubmit"
          />
        </div>
      </div>
    </template>
    <template #controls>
      <div class="flex flex-col gap-6">
        <SettingsHeader
          :heading="t('AILAB.ASSISTANTS.SETTINGS.CONTROL_ITEMS.TITLE')"
          :description="
            t('AILAB.ASSISTANTS.SETTINGS.CONTROL_ITEMS.DESCRIPTION')
          "
        />
        <div class="flex flex-col gap-6">
          <AssistantControlItems
            v-for="item in controlItems"
            :key="item.name"
            :control-item="item"
          />
        </div>
      </div>
    </template>

    <!-- SVO Style Edit Assistant Dialog -->
    <EditAssistantDialog
      v-if="assistant"
      ref="editAssistantDialog"
      :assistant="assistant"
      @update="handleSubmit"
    />
  </SettingsPageLayout>
</template>
